import {Injectable} from '@angular/core';
import {BaseService} from 'src/app/helpers/base.service';
import {Observable, Subject} from 'rxjs';
import {RequestInvoices, Invoice} from '../models/invoice.interface';
import {map} from 'rxjs/operators';
import {InvoiceDraft} from '../models/invoice-draft.interface';
import {HttpClient} from '@angular/common/http';
import {ConnectionService} from '../../helpers/offline-detection';
import {ConfigLoaderService} from '../../config-loader.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../../shared/confirmation-dialog/confirmation.dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {PatientService} from '../../patient-management/services/patient.service';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService extends BaseService {

  private invoiceTransferredRefresh = new Subject();
  public invoiceTransferredRefresh$ = this.invoiceTransferredRefresh.asObservable();
  constructor(
    http: HttpClient,
    connectionService: ConnectionService,
    configService: ConfigLoaderService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private patientService: PatientService
  ) {
    super(
      http,
      connectionService,
      configService
    );
  }

  public getDashboardInvoices(patientId: string, clientId: string, userId: string, startDate: Date, endDate: Date,
                              status: string, invoiceStatus: string, incompleteMedicalRecords: boolean): Observable<Invoice[]> {
    const route = `${this.apiUrl}/Invoice/GetDashboardInvoices`;
    const model = {
      patientId,
      clientId,
      userId,
      startDate,
      endDate,
      status,
      invoiceStatus,
      incompleteMedicalRecords
    } as RequestInvoices;
    const body = JSON.stringify(model);
    return this.http.post<Invoice[]>(route, body, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public getInvoice(invoiceId: string): Observable<Invoice> {
    const route = `${this.apiUrl}/Invoice/${invoiceId}/GetInvoice`;
    return this.http.get<Invoice>(route, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public trackRevisions(invoiceId: string): Observable<any> {
    const route = `${this.apiUrl}/Invoice/${invoiceId}/TrackRevisions`;
    return this.http.get<Invoice>(route, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public setInvoiceStatus(invoiceId: string, invoiceStatus: string): Observable<any> {
    const route = `${this.apiUrl}/Invoice/${invoiceId}/SetStatus/${invoiceStatus}`;
    return this.http.post<any>(route, null, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public deleteInvoice(invoiceId: string): Observable<null> {
    const route = `${this.apiUrl}/Invoice/${invoiceId}/DeleteInvoice`;
    return this.http.post<null>(route, null, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public mergeInvoices(firstInvoiceId: string, secondInvoiceId: string): Observable<null> {
    const route = `${this.apiUrl}/Invoice/${firstInvoiceId}/Merge/${secondInvoiceId}`;

    return this.http.post<null>(route, null, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public getPatientInvoices(clientId: string, patientId: string): Observable<Invoice[]> {

    const route = `${this.apiUrl}/Invoice/ClientInvoices/${clientId}/Patient/${patientId}`;

    return this.http.get<Invoice[]>(route, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  // Auto save
  public autoSave(invoiceDraft: InvoiceDraft): Observable<InvoiceDraft> {
    const route = `${this.apiUrl}/Invoice/AutoSave`;
    const body = JSON.stringify(invoiceDraft);
    return this.http.post<InvoiceDraft>(route, body, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public discardDraft(invoiceDraft: InvoiceDraft): Observable<InvoiceDraft> {
    const route = `${this.apiUrl}/Invoice/DiscardInvoices/${invoiceDraft.id}`;
    return this.http.post<InvoiceDraft>(route, null, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public savedInvoices(userId: string): Observable<InvoiceDraft[]> {
    const route = `${this.apiUrl}/Invoice/SavedInvoices/${userId}`;
    return this.http.get<InvoiceDraft[]>(route, this.headers()).pipe(map(response => {
      return response;
    }));
  }

  public transferInvoice(invoiceId: string, patientId): Observable<Invoice> {
    const route = `${this.apiUrl}/Invoice/TransferInvoice`;
    const body = {
      invoiceId,
      patientId
    };
    return this.http.post<Invoice>(route, body, this.headers()).pipe(map(response => {
      this.invoiceTransferredRefresh.next();
      return response;
    }));
  }

  public euthanasiaDialog(patient: IEuthDialogPatient) {
    if (
      patient == null ||
      patient.patientStatus === 'Deceased'
    ) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        header: 'WARNING: Euthanasia',
        body: `You are about to change ${patient.name}'s status to deceased.`,
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res != null) {
        this.patientService.euthenize(patient.id).subscribe(
          () => {
            patient.patientStatus = 'Deceased';
            this.snackBar.open('Patient status was updated.', 'Success', {
              duration: 2000,
            });
          },
          (error) => {
            this.snackBar.open('Patient could not be updated.', 'Error', {
              duration: 2000,
            });
          },
        );
      }
    });
  }
}

interface IEuthDialogPatient {
  id: string;
  name: string;
  patientStatus: string;
}
